import { useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import { NavigationHeader } from '@/components/NavigationHeader/NavigationHeader'
import { Body1 } from '@/components/ui/typography'
import { UserProfileSessionPage } from '@/routes/Profile/UserSessionPage'
import { ChangePassword } from 'api/Identity/Types/ChangePassword'
import { UpdateUserProfile } from 'api/Identity/Types/UpdateUserProfile'
import { TokenStorage } from 'api/tokenStorage'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchDateFormatAction } from 'store/identity/identity.actions'
import {
  DEFAULT_TIME_ZONE,
  persistor,
  PREFERRED_DATE_FORMAT,
  PREFERRED_TIME_ZONE,
} from 'store/store'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { sleep } from 'utils/sleep'
import { getTimeZoneById, getTimeZoneId, getTimeZones } from 'utils/time-zone/helpers'

import { UserProfilePasswordForm } from './UserProfilePasswordForm'
import { UserProfileSettingsForm } from './UserProfileSettingsForm'

export const UserProfilePage = () => {
  const dispatch = useAppDispatch()
  const setToast = useContext(ToastContext)
  const dateFormatTypes = useAppSelector((state) => state.identity.dateFormat)
  const navigate = useNavigate()

  const [auth, setAuth] = useContext(AuthSessionContext)
  const identityApiClient = useApiClient(IdentityApiClient)

  useEffect(() => {
    if (!!auth && isEmpty(dateFormatTypes)) {
      dispatch(fetchDateFormatAction(identityApiClient))
    }
  }, [auth, dateFormatTypes]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (values: UpdateUserProfile) => {
    await identityApiClient.updateProfile({
      dateFormatTypeId: values.dateFormatTypeId,
    })

    const user = await identityApiClient.getUserBySession()
    const permissions = await identityApiClient.getAuthUserPermission()
    setAuth({ user, permissions })
    localStorage.setItem(PREFERRED_DATE_FORMAT, user.dateFormatType.name)
    localStorage.setItem(
      PREFERRED_TIME_ZONE,
      getTimeZoneById(values?.timeZoneId || DEFAULT_TIME_ZONE)
    )
    setToast(successToast('Date Format updated successfully.'))
  }

  const onChangePassword = async (values: ChangePassword) => {
    await identityApiClient.updateProfilePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    })
    setToast(successToast('Password updated successfully. You will be sign out.'))

    await sleep(1000)

    await persistor.purge()

    setAuth(null)
    TokenStorage.clear()
    navigate(links.login.url)
  }

  if (!auth) return <>bob</>

  return (
    <>
      <NavigationHeader>
        <Body1 bold>User Profile</Body1>
      </NavigationHeader>
      <DataGrid>
        <DataColumn>
          <UserProfileSettingsForm
            user={auth.user}
            dateFormat={dateFormatTypes}
            timeZoneId={getTimeZoneId()}
            timeZones={getTimeZones()}
            onSubmit={onSubmit}
          />
        </DataColumn>
        <DataColumn>
          <UserProfilePasswordForm onSubmit={onChangePassword} />
          <UserProfileSessionPage />
        </DataColumn>
      </DataGrid>
    </>
  )
}
